import { Fragment, useState } from "react";
import {
  Paper,
  Table,
  Checkbox,
  Group,
  Text,
  Button,
  ScrollArea,
  Badge,
  ActionIcon,
  Tooltip,
  Loader,
  Stack,
  Title,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconArrowRight,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { grInvoiceAutomationApi } from "../../../api/grInvoiceAutomation.api";
import { formatDate, fmtMoney } from "./helpers";

function GRLinesTable({ docEntry }) {
  const { data: lines, isLoading } = useQuery({
    queryKey: ["gr", "lines", docEntry],
    queryFn: () => grInvoiceAutomationApi.lines([docEntry]),
    staleTime: 5 * 60 * 1000,
  });
  if (isLoading) {
    return (
      <Group p="md">
        <Loader size="sm" /> <Text c="dimmed">Loading lines…</Text>
      </Group>
    );
  }
  if (!lines || lines.length === 0) {
    return (
      <Text c="dimmed" p="md">
        No lines.
      </Text>
    );
  }
  return (
    <ScrollArea.Autosize mah={360}>
      <Table striped withColumnBorders fz="xs" verticalSpacing={6}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>#</Table.Th>
            <Table.Th>Item Code</Table.Th>
            <Table.Th>Description</Table.Th>
            <Table.Th>Arabic</Table.Th>
            <Table.Th>Barcode</Table.Th>
            <Table.Th ta="right">Qty</Table.Th>
            <Table.Th ta="right">Price</Table.Th>
            <Table.Th ta="right">Total</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {lines.map((ln) => (
            <Table.Tr key={`${ln.DocEntry}-${ln.LineNum}`}>
              <Table.Td c="dimmed">{ln.LineNum}</Table.Td>
              <Table.Td ff="monospace">{ln.ItemCode}</Table.Td>
              <Table.Td>{ln.Dscription}</Table.Td>
              <Table.Td dir="rtl">{ln.ArabicDescription || "—"}</Table.Td>
              <Table.Td ff="monospace">{ln.CodeBars}</Table.Td>
              <Table.Td ta="right">{Number(ln.Quantity)}</Table.Td>
              <Table.Td ta="right">{fmtMoney(ln.Price)}</Table.Td>
              <Table.Td ta="right" ff="monospace">
                {fmtMoney(ln.LineTotal)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </ScrollArea.Autosize>
  );
}

export function ResultsTable({
  headers,
  selected,
  setSelected,
  onGenerate,
  generating,
}) {
  const [expanded, setExpanded] = useState(null);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allSelected =
    headers.length > 0 && selected.size === headers.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(headers.map((h) => h.DocEntry)));
  };

  return (
    <Paper p={0}>
      <Group justify="space-between" p="md">
        <div>
          <Title order={5}>
            {headers.length} GR{headers.length === 1 ? "" : "s"} found
          </Title>
          <Text size="sm" c="dimmed">
            {selected.size > 0
              ? `${selected.size} selected`
              : "Tick the rows you want to push to Duvo"}
          </Text>
        </div>
        <Group gap="xs">
          <Button variant="default" onClick={toggleAll}>
            {allSelected ? "Deselect all" : "Select all"}
          </Button>
          <Button
            rightSection={<IconArrowRight size={16} />}
            disabled={selected.size === 0}
            loading={generating}
            onClick={onGenerate}
          >
            Generate JSON ({selected.size})
          </Button>
        </Group>
      </Group>

      <ScrollArea.Autosize mah={520}>
        <Table striped highlightOnHover verticalSpacing="sm" stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 32 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={toggleAll}
                />
              </Table.Th>
              <Table.Th>GR #</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th>Supplier</Table.Th>
              <Table.Th>Store</Table.Th>
              <Table.Th ta="center">Lines</Table.Th>
              <Table.Th ta="right">Total</Table.Th>
              <Table.Th style={{ width: 56 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {headers.map((h) => {
              const isExpanded = expanded === h.DocEntry;
              const isSelected = selected.has(h.DocEntry);
              return (
                <Fragment key={h.DocEntry}>
                  <Table.Tr
                    bg={
                      isSelected
                        ? "var(--mantine-color-indigo-light)"
                        : undefined
                    }
                  >
                    <Table.Td>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggle(h.DocEntry)}
                      />
                    </Table.Td>
                    <Table.Td ff="monospace">{h.DocNum}</Table.Td>
                    <Table.Td>{formatDate(h.DocDate)}</Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Badge size="sm" variant="light" ff="monospace">
                          {h.CardCode}
                        </Badge>
                        <Text size="sm" lineClamp={1}>
                          {h.CardName}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td ff="monospace">{h.U_Branch}</Table.Td>
                    <Table.Td ta="center">
                      <Badge variant="default" size="sm">
                        {h.LineCount}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right" ff="monospace">
                      {fmtMoney(h.DocTotal)}
                    </Table.Td>
                    <Table.Td>
                      <Tooltip label={isExpanded ? "Hide lines" : "Show lines"}>
                        <ActionIcon
                          variant="subtle"
                          onClick={() =>
                            setExpanded(isExpanded ? null : h.DocEntry)
                          }
                        >
                          {isExpanded ? (
                            <IconChevronDown size={16} />
                          ) : (
                            <IconChevronRight size={16} />
                          )}
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  </Table.Tr>
                  {isExpanded && (
                    <Table.Tr>
                      <Table.Td colSpan={8} p={0}>
                        <Stack p={0} bg="var(--mantine-color-gray-0)">
                          <GRLinesTable docEntry={h.DocEntry} />
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea.Autosize>
    </Paper>
  );
}
